import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Load from "../common/Loader/Load";
import UserMora from "../common/UserMora";
import BidRequestDrawer from "../features/bid/components/BidRequestDrawer/BidRequestDrawer";
import { useHotelOffer } from "../features/bid/hooks/useHotelOffer";
import { useUserMora } from "../context/userMoraContext";
import { useHandleTokenPrice } from "../utils/priceUtils";
import { getUserInfo } from "../service/getInfo";
import analyticsService from "../features/bid/services/analyticsService";
import { getGalleryImages } from "../features/bid/utils/imageUtils";
import convenios from "../assets/convenios.json";
import { ReactComponent as Calendar } from "../assets/icons/calendar-fold.svg";
import { ReactComponent as BaggageClaim } from "../assets/icons/baggage-claim.svg";
import "../features/bid/styles/bidPage.css";

const Bid = () => {
  const { id, hotel } = useParams();
  const navigate = useNavigate();
  const { isStateMora } = useUserMora();
  const { handleTokenPrice } = useHandleTokenPrice();

  const handleOffersNavigation = () => {
    navigate("/home", { replace: true });

    setTimeout(() => {
      document.getElementById("nights")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Hoteles que consumen noches (requieren solicitud)
  const ConsumeNoches = [
    "Hotel Sea Horse",
    "Hotel Wayira Beach",
    "Hotel Amazon Leticia",
    "Hotel Blue Cove",
    "Hotel Blue Reef",
    "Hotel Blue tone",
    "Hotel Tower",
    "Hotel Acantilado de la Tierra",
  ];

  // Estado local
  const [user, setUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [actionHotel, setActionHotel] = useState(false);
  const [priceUrl, setPriceUrl] = useState(null);

  // Usar hook personalizado para cargar oferta
  const { offer, loading, error } = useHotelOffer(id);

  // Buscar hotel en convenios
  const Findhotel = convenios.find((h) => h.nombre === hotel);

  // Determinar tipo de oferta
  const tipo =
    offer?.convenio === 1 ? "Convenio con Descuento" : "Redime Noches";

  // Cargar información del usuario
  const getUserInfoUser = useCallback(async () => {
    try {
      const response = await getUserInfo();
      if (response.data.msg === "Token invalid") {
        localStorage.clear();
        navigate("/");
      } else {
        if (Object.keys(response.data.info.afiliado1).length > 0) {
          setUser(response.data.info);
        } else {
          navigate("/usuario");
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  }, [navigate]);

  useEffect(() => {
    getUserInfoUser();
    setPriceUrl(sessionStorage.getItem("priceURL"));
  }, [getUserInfoUser]);

  // Manejar apertura de pestañas de precio
  const abrirDosPestanas = () => {
    handleTokenPrice(isStateMora, user?.validaciones?.mmora);

    const check = localStorage.getItem("priceView");
    if (check !== "true" && priceUrl?.includes("https")) {
      window.open(priceUrl, "_blank");
      localStorage.setItem("priceView", true);
    }

    // Registrar evento en Analytics
    if (offer) {
      analyticsService.trackReservationClick({
        hotelName: offer.hotel,
      });
    }
  };

  // Crear enlace de reserva
  const createReservationLink = () => {
    return (
      <button
        onClick={() => {
          if (isStateMora !== "1" && Findhotel?.url) {
            window.open(Findhotel.url, "_blank", "noopener noreferrer");
          }
          abrirDosPestanas();
        }}
        className="bid-button-secondary"
      >
        <Calendar className="bid-button-icon" />
        <span>Reservar</span>
      </button>
    );
  };

  // Manejar éxito de solicitud
  const handleRequestSuccess = () => {
    // Registrar evento en Analytics
    if (user && offer) {
      analyticsService.trackBidRequest({
        userName: user.afiliado1.titular,
        offerTitle: offer.titulo,
      });
    }
  };

  // Manejar apertura del drawer
  const handleOpenDrawer = () => {
    if (isStateMora === "1") {
      setActionHotel(true);
    } else {
      setIsDrawerOpen(true);
    }
  };

  if (loading || !offer) {
    return (
      <div className="bid-page">
        <Load />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bid-page">
        <div className="bid-container">
          <h1 className="bid-title">Error al cargar la oferta</h1>
          <p className="bid-description">{error}</p>
        </div>
      </div>
    );
  }

  // Obtener imágenes de la galería (soporta ambos formatos: img1-img5 y array)
  const galleryImages = getGalleryImages(offer, 5);

  return (
    <div className="bid-page">
      <div className="bid-container">
        {/* Breadcrumbs */}
        <nav className="bid-breadcrumbs">
          <a href="/">Inicio</a>
          <span className="bid-breadcrumbs-separator">/</span>
          <button
            type="button"
            className="bid-breadcrumbs-link"
            onClick={handleOffersNavigation}
          >
            Ofertas
          </button>

          <span className="bid-breadcrumbs-separator">/</span>
          <span className="bid-breadcrumbs-current">{offer.titulo}</span>
        </nav>

        {/* Header Section */}
        <div className="bid-header">
          <div className="bid-header-info">
            <h1 className="bid-title">{offer.titulo}</h1>
            <h3 className="bid-subtitle">{offer.hotel}</h3>
          </div>

          <div className="bid-header-actions">
            {/* Botón de solicitud para hoteles que consumen noches */}
            {ConsumeNoches.find((h) => h === hotel) && (
              <button className="request-button" onClick={handleOpenDrawer}>
                <span>Solicitar Reserva</span>
                <span className="material-symbols-outlined button-icon">
                  <BaggageClaim />
                </span>
              </button>
            )}

            {/* Botón de reserva para hoteles con URL */}
            {Findhotel?.url?.length > 0 && createReservationLink()}
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="bid-gallery">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className={`bid-gallery-item ${
                index === 0 ? "bid-gallery-item-large" : ""
              }`}
            >
              <div className="bid-gallery-overlay"></div>
              <img
                src={image.url}
                alt={image.alt}
                className="bid-gallery-image"
              />
              {index === 4 && galleryImages.length > 5 && (
                <div className="bid-gallery-more">
                  <span className="bid-gallery-more-text">
                    +{galleryImages.length - 5} Fotos
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}

        <span className="section-label">Acerca del hotel</span>
        {/* Content Section */}
        <div className="bid-content">
          <div>
            {/* Descripción del hotel */}
            <article
              className="bid-description"
              dangerouslySetInnerHTML={{
                __html: offer.descripcion,
              }}
            />
          </div>
        </div>

        {/* Modal de mora o Drawer de solicitud */}
        {user && (
          <>
            {user.validaciones.mora === "1" && actionHotel ? (
              <UserMora mmora={user.validaciones.mmora} />
            ) : (
              <BidRequestDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                offer={offer}
                user={user}
                tipo={tipo}
                onSuccess={handleRequestSuccess}
                darkMode={false}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Bid;
